import Button from '@mui/material/Button';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { fDate, fAPIDate, fDateTime } from 'src/utils/format-time';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { ChequeStatus, IChequeListItem } from 'src/types/cheque';

type Props = {
  selected: boolean;
  onEditRow: VoidFunction;
  row: IChequeListItem;
  onDeleteRow: VoidFunction;
  hasPaidDateFilter: boolean;
  // Paid tab: show the paid date in place of the received date.
  showPaidDate?: boolean;
  // Banking tab: show the banking date in place of the received date.
  showBankedDate?: boolean;
};

export default function ChequeTableRow({
  row,
  selected,
  hasPaidDateFilter,
  showPaidDate,
  showBankedDate,
  onEditRow,
  onDeleteRow,
}: Props) {
  const {
    _id,
    chequeNumber,
    customer,
    accountNumber,
    bank,
    chequeDate,
    amountInCents,
    holder,
    status,
    receivedDate,
    paidDate,
    bankedAt,
    paidHistory,
  } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.cheque.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover selected={selected}>
          <TableCell sx={{ display: 'flex', alignItems: 'start' }}>
            <ListItemText
              primary={bank}
              secondary={chequeNumber}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
                typography: 'caption',
              }}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            <ListItemText
              primary={customer}
              secondary={accountNumber ?? '—'}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
                typography: 'caption',
              }}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>{holder}</TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fAPIDate(chequeDate)}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            {fCurrencyRupees(amountInCents)}
          </TableCell>

          <TableCell sx={{ textAlign: 'left' }}>
            <Label
              variant="soft"
              color={
                (status === ChequeStatus.PAID && 'success') ||
                (status === ChequeStatus.RETURN && 'error') ||
                (status === ChequeStatus.SETTLED && 'warning') ||
                (status === ChequeStatus.RECOVERING && 'warning') ||
                (status === ChequeStatus.RECOVERED && 'success') ||
                (status === ChequeStatus.HANDOVER && 'info') ||
                (status === ChequeStatus.IN_HAND && 'default') ||
                (status === ChequeStatus.HOLDING && 'warning') ||
                (status === ChequeStatus.BANKING && 'info') ||
                (status === ChequeStatus.FACTORING && 'secondary') ||
                (status === ChequeStatus.FACTORED && 'primary') ||
                'default'
              }
            >
              {status?.split('_').join(' ')}
            </Label>
          </TableCell>

          {hasPaidDateFilter && (
            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
              {paidHistory && paidHistory?.length > 0 ? paidHistory[0].holder : ''}
            </TableCell>
          )}

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {(showPaidDate && fDate(paidDate)) ||
              (showBankedDate && fDate(bankedAt)) ||
              fDateTime(receivedDate)}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link component={RouterLink} href={paths.dashboard.cheque.view(_id)} mr={2}>
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.UPDATE_CHEQUE,
              AuthPermissions.DELETE_CHEQUE,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_CHEQUE,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_CHEQUE,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            Delete
          </MenuItem>
        )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Cheque"
        content={`Are you sure want to delete ${chequeNumber}? This action will permenantly delete ${chequeNumber}'s details from the system`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
